import axios from "axios";
import { create } from "zustand";
import axiosInstance from "../utils/axiosInstance";

export type DashboardStatsData = {
  momentPaymentTrack: { data: any[] } | null;
  collection: { data: any[] } | null;
  collectionPayment: { data: Record<string, any> } | null;
  successCount: { data: any[] } | null;
  paymentPlans: Record<string, any> | null;
  outstandingCollection: { data: Record<string, any> } | null;
  salesCompare: Record<string, { totalCount: number; data: any[] } | null>;
};

export type DashboardVariant = {
  startDate: string | null;
  endDate: string | null;
  isRestarted: boolean;
};

export type DashboardEntry = {
  data: DashboardStatsData | null;
  computedAt: string | null;
  stale: boolean;
  /** No data yet — show skeletons. */
  loading: boolean;
  /** Data on screen, fresher numbers on the way — spin the button only. */
  refreshing: boolean;
  error: string | null;
  errors: string[];
};

const EMPTY_ENTRY: DashboardEntry = {
  data: null,
  computedAt: null,
  stale: false,
  loading: false,
  refreshing: false,
  error: null,
  errors: [],
};

/** Mirrors the server cache key so one variant maps to exactly one entry. */
export const variantKey = (v: DashboardVariant) =>
  [v.startDate ?? "-", v.endDate ?? "-", v.isRestarted ? "r1" : "r0"].join("|");

const queryFor = (v: DashboardVariant) => {
  const parts: string[] = [];
  // One flag only. `restarted` and `restarted-logs` are different WHERE clauses and
  // each aggregation wants a specific one — the bundle maps this per endpoint.
  if (v.isRestarted) parts.push("restarted=true");
  if (v.startDate) parts.push(`startDate=${encodeURIComponent(v.startDate)}`);
  if (v.endDate) parts.push(`endDate=${encodeURIComponent(v.endDate)}`);
  return parts.length ? `?${parts.join("&")}` : "";
};

// Kept outside store state: these are transport details, not render state.
const controllers = new Map<string, AbortController>();
const pollTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * A cold variant means ten aggregations run at concurrency 2, which on a large
 * `loans` / `loan-logs` can comfortably exceed a minute. Poll briskly at first, then
 * back off, and keep going for ~5 minutes rather than abandoning the page blank.
 */
const POLL_MAX_ATTEMPTS = 70;
const pollDelay = (attempt: number) =>
  attempt < 10 ? 2000 : attempt < 30 ? 4000 : 6000;

type State = {
  entries: Record<string, DashboardEntry>;
  fetch: (v: DashboardVariant, opts?: { force?: boolean }) => Promise<void>;
  /** Stops every poll and request in flight, called when the dashboard unmounts. */
  stopAll: () => void;
};

/** The server backs off after a failed recompute, so waiting longer would only poll for nothing. */
const FAILED_MESSAGE =
  "Some charts couldn't refresh, showing the last saved numbers. Try again in a few minutes.";

export const useDashboardStats = create<State>((set, get) => {
  const patch = (key: string, next: Partial<DashboardEntry>) =>
    set((s) => ({
      entries: {
        ...s.entries,
        [key]: { ...(s.entries[key] ?? EMPTY_ENTRY), ...next },
      },
    }));

  const stopPolling = (key: string) => {
    const timer = pollTimers.get(key);
    if (timer) clearTimeout(timer);
    pollTimers.delete(key);
  };

  /**
   * Polls the bundle until `computedAt` moves past what we already had.
   *
   * The server never blocks on a recompute (Heroku terminates requests at 30s), so
   * a background refresh is observed rather than awaited.
   */
  const poll = (v: DashboardVariant, since: string | null, attempt = 0) => {
    const key = variantKey(v);
    stopPolling(key);

    if (attempt >= POLL_MAX_ATTEMPTS) {
      // Give up loudly. Silently clearing the flags used to leave the charts blank
      // with no explanation and no way to retry.
      patch(key, {
        refreshing: false,
        loading: false,
        error: "Stats are taking longer than expected to build. Try refreshing.",
      });
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await axiosInstance.get(`/stats/dashboard${queryFor(v)}`);
        const body = res.data;

        const advanced =
          body?.computedAt && (!since || body.computedAt !== since);

        if (res.status === 200 && body?.data && advanced) {
          patch(key, {
            data: body.data,
            computedAt: body.computedAt,
            stale: Boolean(body.stale),
            loading: false,
            refreshing: false,
            error: null,
            errors: body.errors ?? [],
          });
          stopPolling(key);
          return;
        }

        // Nothing is being rebuilt any more (done or failed), so stop instead of polling for 5 minutes.
        if (body?.refreshing === false) {
          patch(key, {
            ...(body?.data && { data: body.data, computedAt: body.computedAt }),
            loading: false,
            refreshing: false,
            error: body?.failed?.length ? FAILED_MESSAGE : null,
          });
          stopPolling(key);
          return;
        }

        // Still computing, or same timestamp — keep waiting.
        if (res.status === 200 && body?.data && !get().entries[key]?.data) {
          patch(key, { data: body.data, computedAt: body.computedAt, loading: false });
        }
        poll(v, since, attempt + 1);
      } catch {
        poll(v, since, attempt + 1);
      }
    }, pollDelay(attempt));

    pollTimers.set(key, timer);
  };

  return {
    entries: {},

    stopAll: () => {
      pollTimers.forEach((timer) => clearTimeout(timer));
      pollTimers.clear();
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
    },

    fetch: async (v, opts = {}) => {
      const key = variantKey(v);
      const existing = get().entries[key];

      controllers.get(key)?.abort();
      const controller = new AbortController();
      controllers.set(key, controller);
      stopPolling(key);

      patch(key, {
        loading: !existing?.data,
        refreshing: Boolean(existing?.data),
        error: null,
      });

      const previousComputedAt = existing?.computedAt ?? null;

      try {
        const url = `/stats/${opts.force ? "refresh" : "dashboard"}${queryFor(v)}`;
        const res = opts.force
          ? await axiosInstance.post(url, {}, { signal: controller.signal })
          : await axiosInstance.get(url, { signal: controller.signal });

        const body = res.data;

        // nothing cached and nothing building, the server gave up on it for now
        if (!body?.data && body?.refreshing === false) {
          patch(key, {
            loading: false,
            refreshing: false,
            error: body?.failed?.length ? FAILED_MESSAGE : "Failed to load dashboard stats",
          });
          return;
        }

        // 202 => nothing cached yet for this variant; the server is building it.
        if (res.status === 202 || !body?.data) {
          patch(key, { loading: !existing?.data, refreshing: true });
          poll(v, previousComputedAt);
          return;
        }

        patch(key, {
          data: body.data,
          computedAt: body.computedAt,
          stale: Boolean(body.stale),
          loading: false,
          refreshing: Boolean(body.refreshing),
          error: body.failed?.length ? FAILED_MESSAGE : null,
          errors: body.errors ?? [],
        });

        // Served from a stale copy — newer numbers are being computed behind it.
        if (body.refreshing && !body.throttled) {
          poll(v, body.computedAt);
        }
      } catch (error: any) {
        if (axios.isCancel(error) || error?.name === "CanceledError") return;
        patch(key, {
          loading: false,
          refreshing: false,
          error: error?.message ?? "Failed to load dashboard stats",
        });
      } finally {
        controllers.delete(key);
      }
    },
  };
});
